import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { DashboardStateService } from '@app/services/dashboard-state.service';
import {
  KnittingMachineStatus,
  MachineProductionRate,
  ShiftProductionRate,
} from '@app/models/machine-production-rate.model';
import { staggerSlideIn } from '@app/animations/stagger-entrance.animation';

const STATUS_LABELS: Record<KnittingMachineStatus, string> = {
  InUse: '',
  Idle: 'EN ESPERA',
  Available: 'EN ESPERA',
  Loading: 'ESPERANDO HILO',
  Unloading: 'DESCARGANDO',
  Maintenance: 'MANT. PREV.',
  OutOfService: 'FUERA DE SERVICIO',
};

const DISPLAY_LIMIT = 12;

interface MachineTile extends MachineProductionRate {
  isActive: boolean;
  statusLabel: string;
  barPercent: number;
  tier: 'top' | 'low' | 'normal';
}

@Component({
  selector: 'app-machine-production-rate',
  templateUrl: './machine-production-rate.component.html',
  styleUrl: './machine-production-rate.component.scss',
  animations: [staggerSlideIn],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class MachineProductionRateComponent {
  private readonly stateService = inject(DashboardStateService);

  readonly production = computed<ShiftProductionRate | null>(() =>
    this.stateService.lastData()?.machineProductionRates ?? null
  );

  readonly machines = computed<MachineProductionRate[]>(() =>
    this.production()?.machines ?? []
  );

  readonly producingMachines = computed(() =>
    this.machines().filter(m => m.lbsPerHour > 0)
  );

  readonly shiftAverage = computed(() => {
    const producing = this.producingMachines();
    if (producing.length === 0) return 0;
    const sum = producing.reduce((acc, m) => acc + m.lbsPerHour, 0);
    return Math.round(sum / producing.length);
  });

  readonly topMachine = computed<MachineProductionRate | null>(() => {
    const producing = this.producingMachines();
    if (producing.length === 0) return null;
    return producing.reduce((best, m) => (m.lbsPerHour > best.lbsPerHour ? m : best));
  });

  readonly lowMachine = computed<MachineProductionRate | null>(() => {
    const producing = this.producingMachines();
    if (producing.length === 0) return null;
    return producing.reduce((worst, m) => (m.lbsPerHour < worst.lbsPerHour ? m : worst));
  });

  readonly tiles = computed<MachineTile[]>(() => {
    const top = this.topMachine();
    const topLbs = top?.lbsPerHour ?? 0;
    const avg = this.shiftAverage();
    return [...this.machines()]
      .sort((a, b) => b.lbsPerHour - a.lbsPerHour)
      .slice(0, DISPLAY_LIMIT)
      .map((m): MachineTile => {
        const isActive = m.lbsPerHour > 0;
        let tier: MachineTile['tier'] = 'normal';
        if (isActive) {
          if (m.lbsPerHour > avg) tier = 'top';
          else if (avg > 0 && m.lbsPerHour < avg * 0.85) tier = 'low';
        }
        return {
          ...m,
          isActive,
          statusLabel: m.status ? (STATUS_LABELS[m.status] ?? '') : '',
          barPercent: topLbs > 0 ? Math.round((m.lbsPerHour / topLbs) * 100) : 0,
          tier,
        };
      });
  });

  readonly shiftLabel = computed(() => `Grupo ${this.stateService.currentGroup()}`);
  readonly animationTrigger = computed(() => this.tiles());
}
